import { Component, Emit, Inject, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import ApeironComponent from '@/components/commons/apeiron-component.model';
import { Solucion } from '@/shared/model/solucion.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import ParticipanteService from '@/components/participantes/participante.service';
import { IPersona } from '@/shared/model/persona/persona.model';
import CatalogosSnp from '@/shared/model/snp/catalogos-snp.factory';
import {
  Acta,
  InicioGeneracion,
  IParticipacionSnp,
  MenuActa,
  MenuEmpleadoEn,
  MenuInstitucionSnp,
  MenuOpcionGraduacion,
  MenuSedeSnp,
  MenuTipoBeca,
  MenuTrabaja,
  ParticipacionSnp,
  TipoBeca,
} from '@/shared/model/snp/alumno.model';
import { RolAutoridad } from '@/shared/model/enumerations/rol-autoridad.model';
import { required, minValue, requiredIf, minLength } from 'vuelidate/lib/validators';
import { obtenerFechaHoyYYYYMMDD, obtenerFechaMasMesesUltimoDiaMes, obtenerFechaPrimerDiaMes } from '@/shared/date/date-utils';
import { FiltroParticipacion } from '@/shared/model/filtro-participacion.model';
import { Participante } from '@/components/participantes/participante.model';
import { ALUMNOS_CLAVE_ACTA_NO } from '@/shared/model/snp/snp-constants';
import CatalogosAlumnoUtils from './catalogos-alumno-utils.service';
import { CatalogoEntidades } from '@/shared/model/catalogoEntidades.model';
import { CatalogoMunicipios } from '@/shared/model/catalogoMunicipios.model';

const validations = function () {
  const { alumnoForm } = this as AlumnoRegistroComponent;
  return {
    alumnoForm: {
      participante: {
        cvu: { required },
        curp: { required },
        nombre: { required },
        apellidoPaterno: { required },
        apellidoMaterno: {},
        duracionMeses: {
          required,
          minValue: minValue(1),
        },
        tipoBeca: { required },
        inicioGeneracion: { required },
        inicioEstudios: { required },
        finEstudios: { required },
        obtencionGrado: {},
        reincorporacion: {},
        motivoBaja: {
          required: requiredIf(() => (alumnoForm.participante?.fechaBaja?.length ?? 0) > 0),
        },
        esExtranjero: {},
        entidadFederativa: {
          required: requiredIf(() => !alumnoForm.participante?.esExtranjero),
        },
        municipio: {
          required: requiredIf(() => !alumnoForm.participante?.esExtranjero),
        },
        institucion: { required },
        sede: { required },
        comprobanteEstudios: {},
        comprobanteNacionalidad: {},
        acta: {
          required: requiredIf(() => (alumnoForm.participante?.obtencionGrado?.length ?? 0) > 0),
        },
        folioActa: {
          required: requiredIf(
            () => (alumnoForm.participante?.obtencionGrado?.length ?? 0) > 0 && (alumnoForm.participante?.acta?.clave ?? '') == '1'
          ),
        },
        opcionGraduacion: {
          required: requiredIf(() => (alumnoForm.participante?.obtencionGrado?.length ?? 0) > 0),
        },
        continuarAlGradoSuperior: {
          required: requiredIf(() => (alumnoForm.participante?.obtencionGrado?.length ?? 0) > 0),
        },
        tieneProtocolo: {},
        protocolo: {
          required: requiredIf(() => alumnoForm.participante?.tieneProtocolo),
          minLength: minLength(50),
        },
        tieneImpactoCientifico: {},
        impactoCientifico: {
          required: requiredIf(() => alumnoForm.participante?.tieneImpactoCientifico),
          minLength: minLength(50),
        },
        tieneImpactoSocial: {},
        impactoSocial: {
          required: requiredIf(() => alumnoForm.participante?.tieneImpactoSocial),
          minLength: minLength(50),
        },
        tieneAportacionesSolucionProblemasPrioritarios: {},
        aportacionesSolucionProblemasPrioritarios: {
          required: requiredIf(() => alumnoForm.participante?.tieneAportacionesSolucionProblemasPrioritarios),
          minLength: minLength(50),
        },
        tieneEstrategiaAccesoUniversalConocimiento: {},
        estrategiaAccesoUniversalConocimiento: {
          required: requiredIf(() => alumnoForm.participante?.tieneEstrategiaAccesoUniversalConocimiento),
          minLength: minLength(50),
        },
        trabaja: {},
        empleadoEn: {
          required: requiredIf(() => (alumnoForm.participante?.trabaja?.clave ?? '') == '1'),
        },
      },
    },
  };
};

@Component({ validations: validations })
export default class AlumnoRegistroComponent extends mixins(ApeironComponent, CatalogosAlumnoUtils) {
  @Inject('participanteService') private readonly participanteService!: () => ParticipanteService;

  @Prop({ required: true })
  public solucion!: Solucion;

  @Prop({ required: true })
  public solicitud!: Solicitud;

  @Prop({ required: true })
  public permisos!: any;

  @Prop({ required: true })
  public formModel!: any;

  @Prop({
    required: false,
    default: function () {
      return new ParticipacionSnp();
    },
  })
  public alumnoEditar!: ParticipacionSnp;

  public tiposBeca: Array<MenuTipoBeca> = [];
  public opcionesActa: Array<MenuActa> = [];
  public opcionesGraduacion: Array<MenuOpcionGraduacion> = [];
  public opcionesTrabaja: Array<MenuTrabaja> = [];
  public opcionesEmpleadoEn: Array<MenuEmpleadoEn> = [];

  public alumnoForm: IParticipacionSnp = new ParticipacionSnp();
  private tipoDeBecaOriginal: TipoBeca = {};
  private inicioGeneracionOriginal: string | null = null;
  public fechaHoy = '';

  public instituciones: Array<MenuInstitucionSnp> = [];
  public sedes: Array<MenuSedeSnp> = [];

  public guardando = false;
  public entidadesFederativas: Array<{ value: CatalogoEntidades; text: string }> = [];
  public municipios: Array<{ value: CatalogoMunicipios; text: string }> = [];
  public fechasGeneracion: Array<InicioGeneracion> = [];

  public seleccionarAlumno(alumno: IPersona): void {
    this.validarAlumnoDuplicado(alumno.cvu).then(existeCvu => {
      if (existeCvu) {
        this.alertService().showError(this, this.$t('apeironGwApp.alumnoSnp.form.alumnoDuplicado').toString());
      } else {
        this.iniciarParticipacionDeAlumno(alumno);
      }
    });
  }

  private iniciarParticipacionDeAlumno(alumno: IPersona): void {
    this.alumnoForm = ParticipacionSnp.fromObject(new ParticipacionSnp());
    this.alumnoForm.solicitudId = this.solicitud.id;
    this.alumnoForm.solucionId = this.solicitud.solucionId;
    this.alumnoForm.rol = RolAutoridad.ALUMNO;
    this.alumnoForm.participante = Participante.fromObject(alumno);
    this.alumnoForm.participante.duracionMeses = this.solicitud?.planDeEstudios?.duracionOficial ?? 0;
    this.alumnoForm.participante.clavePrograma = this.solicitud.programa.clave;
    if (this.instituciones.length == 1) {
      this.alumnoForm.participante.institucion = this.instituciones[0].value;
      this.seleccionarInstitucion();
    }
  }

  public mounted(): void {
    this.iniciarComponente(true);
  }

  @Watch('alumnoEditar')
  public watchAlumnoEditar(): void {
    this.iniciarComponente(false);
  }

  public manejadorCampoEsExtranjero(): void {
    if (this.alumnoForm.participante?.esExtranjero) {
      this.alumnoForm.participante.entidadFederativa = undefined;
      this.alumnoForm.participante.municipio = undefined;
    }
  }

  public limpiarDatos(): void {
    this.iniciarComponente(true);
  }

  private iniciarComponente(limpiarForm: boolean): void {
    this.fechaHoy = obtenerFechaHoyYYYYMMDD();
    this.opcionesActa = [];
    CatalogosSnp.obtenerOpcionesDeActa().forEach(opcionActa => {
      this.opcionesActa.push({ value: opcionActa, text: opcionActa.descripcion });
    });
    this.opcionesGraduacion = [];
    CatalogosSnp.obtenerOpcionesDeGraduacion().forEach(opcion => {
      this.opcionesGraduacion.push({ value: opcion, text: opcion.descripcion, disabled: ['1', '2', '3', '6', '7'].includes(opcion.clave) });
    });
    this.tiposBeca = [];
    CatalogosSnp.obtenerTiposDeBecas().forEach(opcion => {
      this.tiposBeca.push({
        value: opcion,
        text: opcion.descripcion,
        disabled: opcion.clave == 'POSTULANTE_BECA' || opcion.clave == '4',
      });
    });
    this.opcionesTrabaja = [];
    CatalogosSnp.obtenerOpcionesDeTrabaja().forEach(opcion => {
      this.opcionesTrabaja.push({ value: opcion, text: opcion.descripcion });
    });
    this.opcionesEmpleadoEn = [];
    CatalogosSnp.obtenerOpcionesDeEmpleadoEn().forEach(opcion => {
      this.opcionesEmpleadoEn.push({ value: opcion, text: opcion.descripcion });
    });

    this.instituciones = [];

    if (this.solicitud?.programa?.instituciones) {
      this.solicitud.programa.instituciones.forEach(institucion => {
        this.instituciones.push({
          value: {
            claveInstitucion: institucion.claveInstitucion,
            nombreInstitucion: institucion.nombreInstitucion,
          },
          text: institucion.nombreInstitucion,
        });
      });
    }

    this.fechasGeneracion = [];

    if (this.solicitud?.generaciones?.length) {
      this.fechasGeneracion = [...this.solicitud.generaciones]
        .sort((a, b) => new Date(a.fechaGeneracion).getTime() - new Date(b.fechaGeneracion).getTime())
        .map(generacion => ({
          value: generacion.fechaGeneracion,
          text: generacion.fechaGeneracion,
        }));
    }

    this.alumnoForm = !limpiarForm && this.alumnoEditar?.id ? ParticipacionSnp.fromObject(this.alumnoEditar) : new ParticipacionSnp();
    this.alumnoForm.participante.duracionMeses = this.solicitud?.planDeEstudios?.duracionOficial ?? 0;
    if (this.alumnoEditar?.participante.tipoBeca?.clave === 'POSTULANTE_BECA') {
      this.alumnoForm.participante.tipoBeca = CatalogosSnp.obtenerTiposDeBecas().find(tipoBeca => tipoBeca.clave === 'POSTULANTE_BECA') ?? {
        clave: '',
        descripcion: '',
      };
    }
    if (!!(this.alumnoForm?.participante?.inicioGeneracion ?? undefined) && !(this.alumnoForm?.participante?.inicioEstudios ?? undefined)) {
      this.onChangeInicioGeneracion(this.alumnoForm.participante.inicioGeneracion);
    }
    this.tipoDeBecaOriginal = TipoBeca.fromObject(this.alumnoForm.participante?.tipoBeca ?? new TipoBeca());
    this.inicioGeneracionOriginal = this.alumnoForm.participante?.inicioGeneracion ?? null;

    this.seleccionarInstitucion();

    this.cargarEntidades();
    if (this.alumnoForm.participante?.entidadFederativa?.id ?? undefined) {
      this.cargarMunicipios(this.alumnoForm.participante.entidadFederativa.id);
    }
  }

  public guardarAlumno(): void {
    if (
      this.tiposBeca
        .filter(opcion => opcion.disabled)
        .map(opcion => opcion.value?.clave)
        .includes(this.alumnoForm.participante?.tipoBeca?.clave ?? '--') &&
      this.tipoDeBecaOriginal.clave != this.alumnoForm.participante?.tipoBeca?.clave
    ) {
      this.alertService().showWarning(this, this.$t('apeironGwApp.alumnoSnp.form.tipoBecaInactivo').toString());
      return;
    }

    if (this.tipoDeBecaOriginal.clave != null && this.tipoDeBecaOriginal.clave != this.alumnoForm.participante?.tipoBeca?.clave) {
      this.alertService().showWarning(this, this.$t('apeironGwApp.alumnoSnp.form.tipoBecaFija').toString());
      return;
    }

    if (
      this.inicioGeneracionOriginal != null &&
      this.inicioGeneracionOriginal != (this.alumnoForm.participante?.inicioGeneracion ?? null) &&
      (this.alumnoForm.participante?.tipoBeca?.clave ?? '') != 'SIN_BECA'
    ) {
      this.alertService().showWarning(this, this.$t('apeironGwApp.alumnoSnp.form.inicioGeneracionFijo').toString());
      return;
    }

    this.onChangeInicioGeneracion(this.alumnoForm.participante?.inicioGeneracion ?? '');

    this.guardando = true;

    if (!this.formularioIsInvalid) {
      if (this.esEscritura) {
        this.alumnoForm.contenedorId =
          this.alumnoForm.participante.institucion.claveInstitucion + '-' + this.alumnoForm.participante.sede.claveSede;

        if (this.alumnoForm.id) {
          this.participanteService()
            .update(this.alumnoForm)
            .then(() => {
              this.iniciarComponente(true);
              this.registrarAlumno();
            })
            .catch(error => {
              this.alertService().showHttpError(this, error.response);
              this.guardando = false;
            })
            .finally(() => {
              this.guardando = false;
            });
        } else {
          this.participanteService()
            .create(this.alumnoForm, this.solucion.id)
            .then(() => {
              this.iniciarComponente(true);
              this.registrarAlumno();
            })
            .catch(error => {
              if (error.response.status === 409) {
                this.alertService().showError(this, this.$t('apeironGwApp.alumnoSnp.form.alumnoRegistradoPrograma').toString());
                return;
              }
              this.alertService().showHttpError(this, error.response);
            })
            .finally(() => {
              this.guardando = false;
            });
        }
      } else {
        this.guardando = false;
      }
    } else {
      this.guardando = false;
      this.alertService().showWarning(this, this.$t('apeironGwApp.alumnoSnp.form.formularioInvalido').toString());
    }
  }

  @Emit('guardarAlumno')
  public registrarAlumno(): ParticipacionSnp {
    return ParticipacionSnp.fromObject(this.alumnoForm);
  }

  public seleccionarTrabaja(): void {
    if (this.alumnoForm.participante?.trabaja?.clave == '2') {
      this.alumnoForm.participante.empleadoEn = undefined;
    }
  }

  public onChangeInicioGeneracion(inicioGeneracion: string): void {
    const item = this.fechasGeneracion.find(op => op.value === inicioGeneracion);

    this.alumnoForm.participante.inicioEstudios = obtenerFechaPrimerDiaMes(item.text);
    this.alumnoForm.participante.finEstudios = obtenerFechaMasMesesUltimoDiaMes(item.text, this.alumnoForm.participante.duracionMeses);
  }

  public seleccionarInstitucion(): void {
    this.sedes = [];

    if (this.solicitud?.programa?.instituciones) {
      this.solicitud.programa.instituciones.forEach(institucion => {
        if (institucion.claveInstitucion == this.alumnoForm.participante.institucion?.claveInstitucion) {
          institucion.sedes.forEach(sede => {
            this.sedes.push({
              value: {
                claveSede: sede.claveSede,
                claveDependencia: sede.claveDependencia,
                nombreDependencia: sede.nombreDependencia,
                sede: sede.sede,
              },
              text: `${sede.nombreDependencia} (${sede.sede})`,
            });
          });
        }
      });
    }

    if (!this.alumnoForm.participante?.sede) {
      if (this.sedes.length == 1) {
        this.alumnoForm.participante.sede = this.sedes[0].value;
      } else {
        this.alumnoForm.participante.sede = undefined;
      }
    }
  }

  public reiniciaCampoProtocolo(): void {
    if (!this.alumnoForm.participante.tieneProtocolo) {
      this.alumnoForm.participante.protocolo = undefined;
    }
  }

  public reiniciaCampoImpactoCientifico(): void {
    if (!this.alumnoForm.participante.tieneImpactoCientifico) {
      this.alumnoForm.participante.impactoCientifico = undefined;
    }
  }

  public reiniciaCampoImpactoSocial(): void {
    if (!this.alumnoForm.participante.tieneImpactoSocial) {
      this.alumnoForm.participante.impactoSocial = undefined;
    }
  }

  public reiniciaCampoAportacionesSolucionProblemasPrioritarios(): void {
    if (!this.alumnoForm.participante.tieneAportacionesSolucionProblemasPrioritarios) {
      this.alumnoForm.participante.aportacionesSolucionProblemasPrioritarios = undefined;
    }
  }

  public reiniciaCampoEstrategiaAccesoUniversalConocimiento(): void {
    if (!this.alumnoForm.participante.tieneEstrategiaAccesoUniversalConocimiento) {
      this.alumnoForm.participante.estrategiaAccesoUniversalConocimiento = undefined;
    }
  }

  public manejaCampoManejaActa(): void {
    if (this.alumnoForm.participante.acta?.clave == ALUMNOS_CLAVE_ACTA_NO) {
      this.alumnoForm.participante.folioActa = undefined;
    }
  }

  public validarAlumnoDuplicado(cvu: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const filtroPart: FiltroParticipacion = {
        solicitudId: this.solicitud.id,
        rol: RolAutoridad.ALUMNO,
        participanteCvu: cvu,
      };
      const paginationQuery = {
        page: 0,
        size: 1,
        sort: ['id,desc'],
        filter: filtroPart,
      };
      this.participanteService()
        .obtenerParticipaciones(paginationQuery)
        .then(res => resolve((res.data?.length ?? 0) > 0))
        .catch(() => reject(new Error()));
    });
  }

  public async cargarEntidades() {
    this.entidadesFederativas = await this.getAllEntidades();
  }

  public async cargarMunicipios(entidadId: string): Promise<void> {
    this.getMunicipiosByEntidad(entidadId).then((listaMunicipios: Array<{ value: CatalogoMunicipios; text: string }>) => {
      this.municipios = listaMunicipios;
    });
  }
  public onChangeEntidad(entidad: CatalogoEntidades): void {
    this.alumnoForm.participante.municipio = undefined;
    this.getMunicipiosByEntidad(entidad.id).then((listaMunicipios: Array<{ value: CatalogoMunicipios; text: string }>) => {
      this.municipios = listaMunicipios;
    });
  }

  get formularioIsInvalid(): boolean {
    return this.$v.$invalid;
  }

  /**
   * Indicador para permitir el modo escritura
   */
  get esEscritura(): boolean {
    return this.esEscrituraConEstadoEnCaptura(this.solicitud.estado);
  }

  public reiniciaCampoFechaObtencionGrado(): void {
    this.alumnoForm.participante.obtencionGrado = undefined;
    this.alumnoForm.participante.acta = undefined;
    this.alumnoForm.participante.folioActa = undefined;
    this.alumnoForm.participante.opcionGraduacion = undefined;
    this.alumnoForm.participante.continuarAlGradoSuperior = undefined;
  }

  public reiniciaCampoFechaBaja(): void {
    this.alumnoForm.participante.fechaBaja = undefined;
    this.alumnoForm.participante.reincorporacion = undefined;
    this.alumnoForm.participante.motivoBaja = undefined;
  }

  public reiniciaCampoFechaReincorporacion(): void {
    this.alumnoForm.participante.reincorporacion = undefined;
  }
}
